import React, { useState } from 'react';
import { 
  X, 
  MapPin, 
  Phone, 
  Mail, 
  User, 
  Users, 
  Clock, 
  Building2,
  Edit,
  Plus,
  Eye,
  Settings,
  Award,
  Target,
  TrendingUp,
  Calendar,
  Star,
  CheckCircle,
  AlertTriangle,
  Shield,
  Briefcase,
  GraduationCap
} from 'lucide-react';
import { Unit, UnitEmployee } from '../../types';
import { getUnitTypeName } from '../../data/mockData';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';

interface UnitDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  unit: Unit;
  onEdit?: (unit: Unit) => void;
  onAddEmployee?: (unitId: string) => void;
}

const UnitDetailModal: React.FC<UnitDetailModalProps> = ({ 
  isOpen, 
  onClose, 
  unit, 
  onEdit, 
  onAddEmployee 
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'employees' | 'performance' | 'settings'>('overview');

  const getWorkloadColor = (workload: number) => {
    if (workload >= 90) return 'text-red-600 bg-red-100 dark:bg-red-900/20 dark:text-red-400';
    if (workload >= 75) return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/20 dark:text-yellow-400';
    if (workload >= 50) return 'text-green-600 bg-green-100 dark:bg-green-900/20 dark:text-green-400';
    return 'text-blue-600 bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400';
  };

  const getPerformanceColor = (successRate: number) => {
    if (successRate >= 90) return 'text-green-600 dark:text-green-400';
    if (successRate >= 75) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  const getPositionIcon = (position: string) => {
    if (position.includes('Müdür')) return Shield;
    if (position.includes('Kıdemli')) return Award;
    if (position.includes('Uzman')) return Target;
    return User;
  };

  const renderOverview = () => (
    <div className="space-y-6">
      {/* Basic Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Birim Bilgileri</h4>
          <div className="space-y-3">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Birim Adı</p>
              <p className="font-medium text-gray-900 dark:text-white">{unit.name}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Birim Türü</p>
              <p className="font-medium text-gray-900 dark:text-white">{getUnitTypeName(unit.type)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Yönetici</p>
              <p className="font-medium text-gray-900 dark:text-white">{unit.manager}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Açıklama</p>
              <p className="font-medium text-gray-900 dark:text-white">{unit.description || 'Açıklama eklenmemiş'}</p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <h4 className="text-lg font-semibold text-gray-900 dark:text-white">İletişim ve Konum</h4>
          <div className="space-y-3">
            <div className="flex items-center space-x-3">
              <Mail className="w-5 h-5 text-gray-500" />
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Email</p>
                <p className="font-medium text-gray-900 dark:text-white">{unit.email}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Phone className="w-5 h-5 text-gray-500" />
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Telefon</p>
                <p className="font-medium text-gray-900 dark:text-white">{unit.phone}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <MapPin className="w-5 h-5 text-gray-500" />
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Konum</p>
                <p className="font-medium text-gray-900 dark:text-white">{unit.location || 'Belirtilmemiş'}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Clock className="w-5 h-5 text-gray-500" />
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Çalışma Saatleri</p>
                <p className="font-medium text-gray-900 dark:text-white">{unit.workingHours || '09:00 - 18:00'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Performance Metrics */}
      <div>
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Performans Metrikleri</h4>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
            <div className="flex items-center space-x-3">
              <Users className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              <div>
                <p className="text-2xl font-bold text-blue-900 dark:text-blue-300">{unit.totalStudents}</p>
                <p className="text-sm text-blue-700 dark:text-blue-400">Toplam Öğrenci</p>
              </div>
            </div>
          </div>
          <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
            <div className="flex items-center space-x-3">
              <Target className="w-6 h-6 text-green-600 dark:text-green-400" />
              <div>
                <p className="text-2xl font-bold text-green-900 dark:text-green-300">{unit.currentMonthApplications}</p>
                <p className="text-sm text-green-700 dark:text-green-400">Bu Ay</p>
              </div>
            </div>
          </div>
          <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
            <div className="flex items-center space-x-3">
              <Award className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              <div>
                <p className="text-2xl font-bold text-purple-900 dark:text-purple-300">{unit.monthlyTarget}</p>
                <p className="text-sm text-purple-700 dark:text-purple-400">Aylık Hedef</p>
              </div>
            </div>
          </div>
          <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg">
            <div className="flex items-center space-x-3">
              <TrendingUp className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
              <div>
                <p className="text-2xl font-bold text-yellow-900 dark:text-yellow-300">
                  {unit.monthlyTarget > 0 ? Math.round((unit.currentMonthApplications / unit.monthlyTarget) * 100) : 0}%
                </p>
                <p className="text-sm text-yellow-700 dark:text-yellow-400">Hedef Başarı</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Specializations */}
      {unit.specializations && unit.specializations.length > 0 && (
        <div>
          <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Uzmanlık Alanları</h4>
          <div className="flex flex-wrap gap-2">
            {unit.specializations.map((spec, index) => (
              <span
                key={index}
                className="px-3 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400 text-sm rounded-full border border-blue-200 dark:border-blue-600/30"
              >
                {spec}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Capacity Information */}
      <div>
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Kapasite Bilgileri</h4>
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-600 dark:text-gray-400">Çalışan Kapasitesi</span>
            <span className="font-medium text-gray-900 dark:text-white">
              {unit.employees.length}/{unit.capacity || 'Sınırsız'}
            </span>
          </div>
          {unit.capacity && (
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div 
                className="bg-blue-500 h-2 rounded-full transition-all duration-500" 
                style={{ width: `${Math.min((unit.employees.length / unit.capacity) * 100, 100)}%` }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderEmployees = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
          Çalışanlar ({unit.employees.length})
        </h4>
        <button 
          onClick={() => onAddEmployee?.(unit.id)}
          className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>Çalışan Ekle</span>
        </button>
      </div>

      {/* Employees Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {unit.employees.map((employee) => {
          const PositionIcon = getPositionIcon(employee.position);
          
          return (
            <div key={employee.id} className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                    <span className="text-white font-semibold">
                      {employee.name.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                  <div>
                    <h5 className="font-semibold text-gray-900 dark:text-white">{employee.name}</h5>
                    <div className="flex items-center space-x-2">
                      <PositionIcon className="w-4 h-4 text-gray-500" />
                      <p className="text-sm text-gray-600 dark:text-gray-400">{employee.position}</p>
                    </div>
                  </div>
                </div>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  employee.isActive 
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                    : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                }`}>
                  {employee.isActive ? 'Aktif' : 'Pasif'}
                </span>
              </div>

              <div className="space-y-3 mb-4">
                <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                  <Mail className="w-4 h-4" />
                  <span>{employee.email}</span>
                </div>
                {employee.phone && (
                  <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                    <Phone className="w-4 h-4" />
                    <span>{employee.phone}</span>
                  </div>
                )}
                <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                  <Calendar className="w-4 h-4" />
                  <span>Başlangıç: {format(employee.joinDate, 'dd MMM yyyy', { locale: tr })}</span>
                </div>
              </div>

              {/* Workload */}
              {employee.workload !== undefined && (
                <div className="mb-4">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600 dark:text-gray-400">İş Yükü</span>
                    <span className={`font-medium ${
                      employee.workload >= 90 ? 'text-red-600 dark:text-red-400' :
                      employee.workload >= 75 ? 'text-yellow-600 dark:text-yellow-400' :
                      'text-green-600 dark:text-green-400'
                    }`}>
                      %{employee.workload}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full transition-all duration-500 ${
                        employee.workload >= 90 ? 'bg-red-500' :
                        employee.workload >= 75 ? 'bg-yellow-500' : 'bg-green-500'
                      }`}
                      style={{ width: `${employee.workload}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Performance */}
              {employee.performance && (
                <div className="mb-4">
                  <h6 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Bu Ay Performans</h6>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <p className="text-lg font-bold text-gray-900 dark:text-white">{employee.performance.monthlyTarget}</p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">Hedef</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-gray-900 dark:text-white">{employee.performance.achieved}</p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">Gerçekleşen</p>
                    </div>
                    <div>
                      <p className={`text-lg font-bold ${getPerformanceColor(employee.performance.successRate)}`}>
                        %{employee.performance.successRate}
                      </p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">Başarı</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Responsible Universities */}
              {employee.responsibleUniversities && employee.responsibleUniversities.length > 0 && (
                <div className="mb-4">
                  <h6 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                    Sorumlu Üniversiteler ({employee.responsibleUniversities.length})
                  </h6>
                  <div className="flex flex-wrap gap-1">
                    {employee.responsibleUniversities.slice(0, 3).map((uni, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400 text-xs rounded-full"
                      >
                        {uni}
                      </span>
                    ))}
                    {employee.responsibleUniversities.length > 3 && (
                      <span className="px-2 py-1 bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 text-xs rounded-full">
                        +{employee.responsibleUniversities.length - 3}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center space-x-2">
                <button className="flex-1 flex items-center justify-center space-x-2 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/20 dark:hover:bg-blue-900/30 text-blue-600 dark:text-blue-400 px-3 py-2 rounded-lg transition-colors">
                  <Eye className="w-4 h-4" />
                  <span>Detay</span>
                </button>
                <button className="flex-1 flex items-center justify-center space-x-2 bg-green-50 hover:bg-green-100 dark:bg-green-900/20 dark:hover:bg-green-900/30 text-green-600 dark:text-green-400 px-3 py-2 rounded-lg transition-colors">
                  <Edit className="w-4 h-4" />
                  <span>Düzenle</span>
                </button>
                <button className="p-2 text-gray-500 hover:text-purple-600 dark:text-gray-400 dark:hover:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors">
                  <Settings className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {unit.employees.length === 0 && (
        <div className="text-center py-8">
          <Users className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
          <h5 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Henüz çalışan eklenmemiş</h5>
          <p className="text-gray-600 dark:text-gray-400 mb-4">Bu birime çalışan ekleyerek başlayın</p>
          <button 
            onClick={() => onAddEmployee?.(unit.id)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            İlk Çalışanı Ekle
          </button>
        </div>
      )}
    </div>
  );

  const renderPerformance = () => (
    <div className="space-y-6">
      <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Birim Performansı</h4>
      
      {/* Team Performance Summary */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-xl p-6">
        <h5 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Takım Performans Özeti</h5>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center">
            <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">
              {unit.employees.reduce((sum, emp) => sum + (emp.performance?.achieved || 0), 0)}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">Toplam Gerçekleşen</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-purple-600 dark:text-purple-400">
              {unit.employees.reduce((sum, emp) => sum + (emp.performance?.monthlyTarget || 0), 0)}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">Toplam Hedef</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-green-600 dark:text-green-400">
              %{Math.round(unit.employees.reduce((sum, emp) => sum + (emp.performance?.successRate || 0), 0) / unit.employees.length)}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">Ortalama Başarı</p>
          </div>
        </div>
      </div>

      {/* Individual Performance */}
      <div>
        <h5 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Bireysel Performanslar</h5>
        <div className="space-y-4">
          {unit.employees.map((employee) => (
            <div key={employee.id} className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-semibold">
                      {employee.name.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">{employee.name}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{employee.position}</p>
                  </div>
                </div>
                {employee.workload !== undefined && (
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getWorkloadColor(employee.workload)}`}>
                    İş Yükü: %{employee.workload}
                  </span>
                )}
              </div>
              
              {employee.performance && (
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <p className="text-xl font-bold text-gray-900 dark:text-white">{employee.performance.achieved}</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Gerçekleşen</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xl font-bold text-gray-900 dark:text-white">{employee.performance.monthlyTarget}</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Hedef</p>
                  </div>
                  <div className="text-center">
                    <p className={`text-xl font-bold ${getPerformanceColor(employee.performance.successRate)}`}>
                      %{employee.performance.successRate}
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Başarı</p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderSettings = () => (
    <div className="space-y-6">
      <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Birim Ayarları</h4>
      
      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Settings className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5" />
          <div>
            <h5 className="font-medium text-yellow-900 dark:text-yellow-300">Geliştirme Aşamasında</h5>
            <p className="text-sm text-yellow-800 dark:text-yellow-400 mt-1">
              Birim ayarları özelliği yakında kullanıma sunulacaktır. 
              Şu an için birim düzenleme işlemlerini ana birimler sayfasından yapabilirsiniz.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-4">
          <h5 className="font-medium text-gray-900 dark:text-white mb-3">Birim Bilgileri</h5>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Oluşturulma:</span>
              <span className="text-gray-900 dark:text-white">{format(unit.createdAt, 'dd MMM yyyy', { locale: tr })}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Durum:</span>
              <span className={unit.isActive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                {unit.isActive ? 'Aktif' : 'Pasif'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Çalışan Sayısı:</span>
              <span className="text-gray-900 dark:text-white">{unit.employees.length}</span>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-4">
          <h5 className="font-medium text-gray-900 dark:text-white mb-3">Hızlı İşlemler</h5>
          <div className="space-y-2">
            <button className="w-full flex items-center space-x-2 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/20 dark:hover:bg-blue-900/30 text-blue-600 dark:text-blue-400 px-3 py-2 rounded-lg transition-colors">
              <Edit className="w-4 h-4" />
              <span>Birim Bilgilerini Düzenle</span>
            </button>
            <button className="w-full flex items-center space-x-2 bg-green-50 hover:bg-green-100 dark:bg-green-900/20 dark:hover:bg-green-900/30 text-green-600 dark:text-green-400 px-3 py-2 rounded-lg transition-colors">
              <Plus className="w-4 h-4" />
              <span>Yeni Çalışan Ekle</span>
            </button>
            <button className="w-full flex items-center space-x-2 bg-purple-50 hover:bg-purple-100 dark:bg-purple-900/20 dark:hover:bg-purple-900/30 text-purple-600 dark:text-purple-400 px-3 py-2 rounded-lg transition-colors">
              <TrendingUp className="w-4 h-4" />
              <span>Performans Raporu</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/80 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl w-full max-w-5xl mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{unit.name}</h2>
              <p className="text-gray-600 dark:text-gray-400">{getUnitTypeName(unit.type)}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Navigation */}
        <div className="border-b border-gray-200 dark:border-gray-800">
          <nav className="flex space-x-1 p-1">
            {[
              { id: 'overview', label: 'Genel Bakış', icon: Building2 },
              { id: 'employees', label: 'Çalışanlar', icon: Users },
              { id: 'performance', label: 'Performans', icon: TrendingUp },
              { id: 'settings', label: 'Ayarlar', icon: Settings }
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`flex items-center space-x-2 px-4 py-3 rounded-lg transition-colors ${
                    activeTab === tab.id
                      ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Content */}
        <div className="p-6">
          {activeTab === 'overview' && renderOverview()}
          {activeTab === 'employees' && renderEmployees()}
          {activeTab === 'performance' && renderPerformance()}
          {activeTab === 'settings' && renderSettings()}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-800">
          <button
            onClick={onClose}
            className="px-6 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300 transition-colors"
          >
            Kapat
          </button>
          <button
            onClick={() => onEdit?.(unit)}
            className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
          >
            <Edit className="w-4 h-4" />
            <span>Düzenle</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default UnitDetailModal;